// src/modules/moduleDashboards/components/actions/useWidgetView.js
//
// Some widgets are shaped by a context the dashboard page cannot supply well:
// a single branch (End of Day, incoming cash) or a single operating date (End of
// Day again — the page filter is a reporting RANGE, which is the wrong shape for
// "which day am I closing?").
//
// Those widgets carry their own controls, and this hook owns what happens when
// one changes: re-render THIS widget only, through the server's normal clamping,
// and hold the result. The page-level render still seeds the first paint.
import { useCallback, useEffect, useRef, useState } from 'react';
import { renderDashboardWidget } from '../../api';

export function useWidgetView({ widgetId, data }) {
  const [payload, setPayload] = useState(data);
  const [params, setParams] = useState({ branch: data?.branchId || null, businessDate: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // True once the viewer has overridden something in this card; after that a
  // page-level refresh must not silently yank them back to the default view.
  const touched = useRef(false);
  // The live params, read by reload(). State alone would go stale when two
  // controls change back to back (pick a branch, then step the date) — the second
  // call would still see the first render's values and undo the first change.
  const paramsRef = useRef(params);
  const setBoth = (next) => { paramsRef.current = next; setParams(next); };

  useEffect(() => {
    if (touched.current) return;
    setPayload(data);
    setBoth({ ...paramsRef.current, branch: data?.branchId || null });
  }, [data]);

  const reload = useCallback(async (overrides = {}) => {
    if (!widgetId) return;
    const next = { ...paramsRef.current, ...overrides };
    setBoth(next);
    setLoading(true);
    setError(null);
    try {
      const fresh = await renderDashboardWidget({ id: widgetId, ...next });
      if (fresh?.data) {
        setPayload(fresh.data);
        setBoth({ ...next, branch: fresh.data.branchId || next.branch || null });
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not refresh this widget');
    } finally {
      setLoading(false);
    }
  }, [widgetId]);

  // Change one control and refetch in the same step.
  const setParam = useCallback((key, value) => {
    touched.current = true;
    reload({ [key]: value });
  }, [reload]);

  return {
    payload,
    branchId: params.branch,
    businessDate: params.businessDate,
    setBranch: (v) => setParam('branch', v),
    setBusinessDate: (v) => setParam('businessDate', v),
    reload: () => reload(),
    loading,
    error,
  };
}

export default useWidgetView;
